import type { ReactNode } from 'react';

export type ComposerAttachmentStatus = 'staged' | 'uploading' | 'ready' | 'error';

export interface ComposerAttachmentItem {
  id: string;
  name: string;
  type: string;
  size?: number;
  status: ComposerAttachmentStatus;
  progress?: number;
  previewUrl?: string;
  error?: string;
  source:
    | { kind: 'file'; file: File }
    | { kind: 'uploading' }
    | { kind: 'uploaded'; attachmentId: string };
}

export interface ComposerAttachments {
  items: readonly ComposerAttachmentItem[];
  attach: (files: FileList | readonly File[] | null) => void;
  remove: (id: string) => void;
  readyToSend: boolean;
  accept?: string;
  retry?: (id: string) => void;
  /** Image chips own an object-URL lifecycle the app already holds; it renders them. */
  renderImageItem?: (item: ComposerAttachmentItem, index: number) => ReactNode;
}

export type ComposerMergePickedFiles = (
  existing: readonly File[],
  incoming: FileList | readonly File[] | null,
  options: { maxCount: number; oversizedPolicy: 'skip' }
) => { next: File[]; error?: { title: string; description?: string } };

export const composerAttachmentFiles = (
  items: readonly ComposerAttachmentItem[]
): File[] =>
  items.flatMap((item) => (item.source.kind === 'file' ? [item.source.file] : []));

export const formatComposerAttachmentType = (fileName: string): string => {
  const extension = fileName.split('.').pop();
  return extension && extension !== fileName ? extension.toUpperCase() : 'FILE';
};

export interface ComposerUploadedFile {
  uid: string;
  name: string;
  type: string;
  file: { size: number };
  uploadStatus: 'pending' | 'uploading' | 'completed' | 'error';
  uploadProgress?: number;
  previewUrl?: string;
  attachmentId?: string;
  error?: string;
}

const uploadedAttachmentStatus = (
  uploadStatus: ComposerUploadedFile['uploadStatus']
): ComposerAttachmentStatus => {
  if (uploadStatus === 'completed') return 'ready';
  if (uploadStatus === 'error') return 'error';
  return 'uploading';
};

const uploadedAttachmentSource = (
  file: ComposerUploadedFile
): ComposerAttachmentItem['source'] =>
  file.uploadStatus === 'completed' && file.attachmentId
    ? { kind: 'uploaded', attachmentId: file.attachmentId }
    : { kind: 'uploading' };

export const composerUploadedAttachments = (
  files: readonly ComposerUploadedFile[]
): { items: ComposerAttachmentItem[]; readyToSend: boolean } => ({
  items: files.map((file) => ({
    id: file.uid,
    name: file.name,
    type: file.type,
    size: file.file.size,
    status: uploadedAttachmentStatus(file.uploadStatus),
    progress: file.uploadProgress,
    previewUrl: file.previewUrl,
    error: file.error,
    source: uploadedAttachmentSource(file),
  })),
  readyToSend: files.every((file) => file.uploadStatus === 'completed'),
});

export const composerUploadedAttachmentMetadata = (
  items: readonly ComposerAttachmentItem[]
): { name: string; type: string; attachment_id: string }[] =>
  items.flatMap((item) =>
    item.source.kind === 'uploaded'
      ? [{ name: item.name, type: item.type, attachment_id: item.source.attachmentId }]
      : []
  );
